import { useNavigate } from "react-router-dom";
import { usePlanFeatures } from "@/hooks/use-plan-features";
import { useAuth } from "@/contexts/auth.context";
import { useEffect } from "react";

interface FeatureRouteGuardProps {
  feature: string;
  children: React.ReactNode;
  requireSubscription?: boolean;
  fallbackPath?: string;
}

/**
 * Componente para proteger rotas baseado em funcionalidades do plano.
 * Se o usuário não tiver a funcionalidade, redireciona para a aba de planos no My Account.
 * 
 * Uso:
 * <FeatureRouteGuard feature="dashboard-financeiro" fallbackPath="/account?tab=plan">
 *   <FinancialDashboard />
 * </FeatureRouteGuard>
 */
export function FeatureRouteGuard({
  feature,
  children,
  requireSubscription = true,
  fallbackPath = "/account?tab=plan",
}: Readonly<FeatureRouteGuardProps>) {
  const { hasFeature } = usePlanFeatures();
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;

    // Se não está logado, deixa o ProtectedRoute lidar com isso
    if (!user) return;

    // Verificar se tem a feature
    if (!hasFeature(feature, requireSubscription)) {
      // Redirecionar para página de planos no My Account
      navigate(fallbackPath, { replace: true });
    }
  }, [hasFeature, feature, requireSubscription, user, loading, navigate, fallbackPath]);

  // Se não tem a feature, não renderiza nada enquanto redireciona
  if (!hasFeature(feature, requireSubscription)) {
    return null;
  }

  return <>{children}</>;
}

/**
 * SubscriptionRouteGuard - protege rotas que requerem apenas subscription ativa
 */
export function SubscriptionRouteGuard({
  children,
  fallbackPath = "/account?tab=plan",
}: Readonly<{
  children: React.ReactNode;
  fallbackPath?: string;
}>) {
  const { hasSubscription } = usePlanFeatures();
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!user) return;

    if (!hasSubscription) {
      navigate(fallbackPath, { replace: true });
    }
  }, [hasSubscription, user, loading, navigate, fallbackPath]);

  if (!hasSubscription) {
    return null;
  }

  return <>{children}</>;
}

/**
 * RedirectIfHasFeature - redireciona SE o usuário TIVER a feature
 * Útil para páginas que devem ser acessadas apenas SEM determinada feature
 */
export function RedirectIfHasFeature({
  feature,
  redirectTo = "/financial",
}: {
  feature: string;
  redirectTo?: string;
}) {
  const { hasFeature } = usePlanFeatures();
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!user) return;

    if (hasFeature(feature)) {
      navigate(redirectTo, { replace: true });
    }
  }, [hasFeature, feature, user, loading, navigate, redirectTo]);

  return null;
}
